import { jsx as _jsx } from "react/jsx-runtime";
import { useCurrentClientConfig } from '@src/hooks/useCurrentClientConfig';
import { useUserLocale } from '@src/hooks/useUserLocale';
import { SUPPORTED_LOCALES, WeglotWrapper as WeglotWrapperBase } from 'pe-core-frontend';
import { useEffect, useMemo } from 'react';
/**
 * A wrapper component that enables Weglot translation based on the current client configuration
 * and user's language preference.
 *
 * @param {PropsWithChildren} props - The properties passed to the component.
 * @param {React.ReactNode} props.children - The child components to be wrapped.
 *
 * @returns {JSX.Element} The wrapped children components
 */
export const WeglotWrapper = ({ children }) => {
    const { clientConfig, loading } = useCurrentClientConfig();
    const { locale } = useUserLocale();
    // Convert locale from storage format (e.g., 'es_MX') to Weglot format (e.g., 'es-mx')
    const weglotLanguage = useMemo(() => {
        if (!locale)
            return 'en';
        const matchedLocale = SUPPORTED_LOCALES.find(l => l.locale === locale);
        return matchedLocale?.value;
    }, [locale]);
    // Switch to the user's preferred language after Weglot is initialized
    useEffect(() => {
        if (!weglotLanguage)
            return;
        const MAX_RETRIES = 50; // Stop after 5 seconds (50 × 100ms)
        const switchLanguage = (retries = 0) => {
            if (window.Weglot?.initialized && typeof window.Weglot.switchTo === 'function') {
                window.Weglot.switchTo(weglotLanguage);
                // Dispatch event to notify components that language has been switched
                const event = new CustomEvent('weglotLanguageSwitched', {
                    detail: { language: weglotLanguage },
                });
                window.dispatchEvent(event);
            }
            else if (retries < MAX_RETRIES) {
                // Retry after a short delay
                setTimeout(() => switchLanguage(retries + 1), 100);
            }
            else {
                // Give up - Weglot failed to load, user sees English (acceptable fallback)
                console.warn('Weglot failed to initialize after max retries');
            }
        };
        // Wait a bit for Weglot to initialize
        const timeoutId = setTimeout(switchLanguage, 500);
        return () => clearTimeout(timeoutId);
    }, [weglotLanguage]);
    return (_jsx(WeglotWrapperBase, { enabled: !loading && !clientConfig?.extraData?.disableWeglot, currentLanguage: weglotLanguage || 'en', children: children }));
};
